import { useState } from "react";

import { TvSeriesDocumentResponse } from "../../../common/api/catalogue-api/tv-api/TvSeriesDocumentResponse";
import { GetTvSeriesListRequest } from "../api/GetTvSeriesListRequest";
import { useAssetsManageTvSeriesApi } from "../api/useAssetsManageTvSeriesApi";
import { mapToTvSeriesLisingItem, TvSeriesListingItem } from "./TvSeriesListingItem";
import { TvSeriesListingFilters } from "./useTvSeriesListingFilters";

export const useTvSeriesListing = () => {
    const { getTvSeriesList } = useAssetsManageTvSeriesApi();
    const [items, setItems] = useState<TvSeriesListingItem[]>([]);
    const [tvSeriesToMerge, setTvSeriesToMerge] = useState<TvSeriesListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isListLoading, setIsListLoading] = useState<boolean>(true);

    const getData = async (filters: TvSeriesListingFilters): Promise<void> => {
        setIsListLoading(true);

        const query: GetTvSeriesListRequest = createSearchTvSeriesByTitleQuery(filters);

        const response = await getTvSeriesList(query);

        const tvSeriesManagerItems = response.items.map((item: TvSeriesDocumentResponse) =>
            mapToTvSeriesLisingItem(item)
        );

        setItems(tvSeriesManagerItems);
        setTotalItems(response.totalCount);
        setIsListLoading(false);
    };

    const addNewItemToList = (newItem: TvSeriesListingItem, filters: TvSeriesListingFilters) => {
        setItems((prevItems: TvSeriesListingItem[]) => {
            if (prevItems.length >= filters.pageSize) prevItems.pop();

            return [newItem, ...prevItems];
        });
    };

    const clearTvSeriesToMerge = () => {
        setTvSeriesToMerge([]);
    };

    const updateListAfterMerge = (
        sourceTvSeries: TvSeriesListingItem[],
        newTvSeries: TvSeriesListingItem,
        filters: TvSeriesListingFilters
    ) => {
        setItems((prevItems: TvSeriesListingItem[]) => {
            if (filters.includeMerged) {
                if (prevItems.length >= filters.pageSize) prevItems.pop();

                return [newTvSeries, ...prevItems];
            }

            const filteredItems = prevItems.filter(
                (movieItem: TvSeriesListingItem) =>
                    !sourceTvSeries.some(
                        (tvSeriesToRemove: TvSeriesListingItem) => tvSeriesToRemove.id === movieItem.id
                    )
            );

            return [newTvSeries, ...filteredItems];
        });
    };

    const addTvSeriesItemToMergeList = (item: TvSeriesListingItem) => {
        setTvSeriesToMerge((prevTvSeries: TvSeriesListingItem[]) => {
            return [...prevTvSeries, item];
        });
    };

    const removeTvSeriesFromMergeList = (tvSeriesToRemove: TvSeriesListingItem) => {
        setTvSeriesToMerge((prevMovies: TvSeriesListingItem[]) => {
            return prevMovies.filter((tvSeries: TvSeriesListingItem) => tvSeriesToRemove.id !== tvSeries.id);
        });
    };

    return {
        items,
        totalItems,
        isListLoading,
        tvSeriesToMerge,
        getData,
        addNewItemToList,
        updateListAfterMerge,
        clearTvSeriesToMerge,
        addTvSeriesItemToMergeList,
        removeTvSeriesFromMergeList
    };
};

const createSearchTvSeriesByTitleQuery = (filters: TvSeriesListingFilters) => {
    return {
        page: filters.page,
        pageSize: filters.pageSize,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        title: filters.title,
        includeMerged: filters.includeMerged,
        sources: filters.sources
    };
};
